import { motion } from 'framer-motion'

const PhoneMockup = () => {
  return (
    <div className="relative">
      <div className="w-80 h-[640px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl border-2 border-gray-700">
        <div className="w-full h-full bg-black rounded-[2.5rem] overflow-hidden border border-gray-800">
          <motion.div 
            className="p-5"
            animate={{ y: [0, -400, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
          >
            {/* Status Bar */}
            <div className="flex justify-between items-center mb-6 text-sm font-semibold text-white">
              <span>9:41</span>
              <div className="flex gap-1">
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
              </div>
            </div>

            {/* App Content */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="text-sm text-gray-400 mb-2"
            >
              Gesamtguthaben
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-4xl font-bold mb-1 text-red-600"
            >
              -€487.23
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-sm text-red-600 mb-6 flex items-center gap-1"
            >
              <motion.span 
                animate={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="text-lg"
              >
                ⚠️
              </motion.span> Im Dispo
            </motion.div>

            {/* Chart - Downward trend showing negative balance */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="mb-6 h-32 relative"
            >
              <svg viewBox="0 0 300 120" className="w-full h-full">
                <defs>
                  <linearGradient id="chart-grad-red" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" style={{ stopColor: '#EF4444', stopOpacity: 0.3 }} />
                    <stop offset="100%" style={{ stopColor: '#EF4444', stopOpacity: 0 }} />
                  </linearGradient>
                </defs>
                {/* Downward trending line */}
                <path
                  d="M 0 20 Q 75 40, 150 60 T 300 100"
                  fill="none"
                  stroke="#EF4444"
                  strokeWidth="3"
                />
                <path
                  d="M 0 20 Q 75 40, 150 60 T 300 100 L 300 120 L 0 120 Z"
                  fill="url(#chart-grad-red)"
                />
                {/* Zero line */}
                <line
                  x1="0"
                  y1="60"
                  x2="300"
                  y2="60"
                  stroke="#9CA3AF"
                  strokeWidth="1"
                  strokeDasharray="5,5"
                  opacity="0.5"
                />
              </svg>
            </motion.div>

            {/* AI Warning */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.5 }}
              className="flex gap-3 p-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl mb-6 border border-red-200"
            >
              <div className="text-xl">⚠️</div>
              <div>
                <div className="text-xs font-bold mb-1 text-red-700">Dispo-Warnung</div>
                <div className="text-xs text-gray-600">
                  Du bist im Dispo. Reduziere Ausgaben um €150/Monat.
                </div>
              </div>
            </motion.div>

            {/* Spending Categories */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-sm font-bold text-gray-300 mb-3">Ausgaben nach Kategorie</div>
              <div className="space-y-3">
                {/* Alimentation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0, duration: 0.4 }}
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">🍔</span>
                      <span className="text-xs font-semibold">Lebensmittel</span>
                    </div>
                    <span className="text-xs font-bold text-gray-300">€312.45</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: '45%' }}
                      transition={{ delay: 1.1, duration: 0.8 }}
                      className="h-full bg-gradient-to-r from-orange-400 to-orange-500"
                    />
                  </div>
                </motion.div>

                {/* Divertissement */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2, duration: 0.4 }}
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">🎮</span>
                      <span className="text-xs font-semibold">Unterhaltung</span>
                    </div>
                    <span className="text-xs font-bold text-gray-300">€189.99</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: '27%' }}
                      transition={{ delay: 1.3, duration: 0.8 }}
                      className="h-full bg-gradient-to-r from-blue-400 to-blue-500"
                    />
                  </div>
                </motion.div>

                {/* Transport */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.4, duration: 0.4 }}
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">🚗</span>
                      <span className="text-xs font-semibold">Transport</span>
                    </div>
                    <span className="text-xs font-bold text-gray-300">€125.80</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: '18%' }}
                      transition={{ delay: 1.5, duration: 0.8 }}
                      className="h-full bg-gradient-to-r from-green-400 to-green-500"
                    />
                  </div>
                </motion.div>

                {/* Autres */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.6, duration: 0.4 }}
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">📦</span>
                      <span className="text-xs font-semibold">Sonstiges</span>
                    </div>
                    <span className="text-xs font-bold text-gray-300">€68.23</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: '10%' }}
                      transition={{ delay: 1.7, duration: 0.8 }}
                      className="h-full bg-gradient-to-r from-purple-400 to-purple-500"
                    />
                  </div>
                </motion.div>
              </div>
            </motion.div>

            {/* Recent Transactions */}
            <div className="mb-6">
              <div className="text-sm font-bold text-gray-300 mb-3">Letzte Transaktionen</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center text-xs">🛒</div>
                    <div>
                      <div className="text-xs font-semibold">REWE</div>
                      <div className="text-xs text-gray-500">Heute</div>
                    </div>
                  </div>
                  <div className="text-xs font-bold text-red-600">-€45.67</div>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-xs">🛍️</div>
                    <div>
                      <div className="text-xs font-semibold">Zalando</div>
                      <div className="text-xs text-gray-500">Gestern</div>
                    </div>
                  </div>
                  <div className="text-xs font-bold text-red-600">-€89.99</div>
                </div>
                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center text-xs">🛒</div>
                    <div>
                      <div className="text-xs font-semibold">Lidl</div>
                      <div className="text-xs text-gray-500">Gestern</div>
                    </div>
                  </div>
                  <div className="text-xs font-bold text-red-600">-€32.45</div>
                </div>
              </div>
            </div>

            {/* Accounts */}
            <div className="space-y-2">
              <div className="text-sm font-bold text-gray-300 mb-3">Konten</div>
              <div className="flex items-center gap-3 p-2 bg-red-50 rounded-xl border border-red-200">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-red-600"></div>
                <div className="flex-1">
                  <div className="text-xs font-semibold">Sparkasse</div>
                  <div className="text-xs text-gray-500">DE89 ***7890</div>
                </div>
                <div className="text-xs font-bold text-red-600">-€487.23</div>
              </div>

              <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600"></div>
                <div className="flex-1">
                  <div className="text-xs font-semibold">Deutsche Bank</div>
                  <div className="text-xs text-gray-500">DE45 ***2341</div>
                </div>
                <div className="text-xs font-bold">€0.00</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default PhoneMockup
